// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Bar } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import zoomPlugin from "chartjs-plugin-zoom";
import { dismissTooltipPlugin, verticalLineIntersect } from "./helpers/GraphPlugins";
// import { verticalLinePlugin } from "./alertEventPlugin";
import { setAlertHighlightIndex } from "../../store/forecastSlice";
import "chartjs-adapter-date-fns";
import {
  applyMobileTicks,
  buildXAxisTicks,
  buildZoomOptions,
  createMonthDayTickCallback,
  createTickCallback,
  TIME_RANGES,
} from "./graphutil";
import { buildTooltipOptions, formatDateTimeLabel } from "./forecastUtil";
import useIsMobile from "../../hooks/useIsMobile";
import GraphDesktopControls from "./helpers/GraphDesktopControls";
import GraphMobileTimeLabel from "./helpers/GraphMobileTimeLabel";
import GraphModals from "./helpers/GraphModals";
import useGraphControls from "./helpers/useGraphControls";
import { generatePercentileColors } from "../../utils/colorUtils";

ChartJS.register(...registerables, annotationPlugin, zoomPlugin);

const PrecipPercentile = ({
  data,
  labels,
  highResData,
  control,
  average,
  graphCastData,
  alerts = [],
  timeZone,
  yaxis = "Precipitation (mm)",
  title = "Precipitation Forecast",
  onTimeZoneChange,
  onRegisterControls,
  desktopExtraControls,
  hideGraphControls = false,
}) => {
  const dispatch = useDispatch();
  const forecast = useSelector((state) => state.forecast);
  const isMobile = useIsMobile();
  const chartRef = React.useRef(null);
  const currentViewRef = React.useRef({
    start: new Date(),
    end: new Date()
  });
  const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const [isTimeRangeModalOpen, setIsTimeRangeModalOpen] = useState(false);
  const [visibleDatasets, setVisibleDatasets] = useState({});
  const [selectedTimeRange, setSelectedTimeRange] = useState('full');
  const [isZoomed, setIsZoomed] = useState(false);

  useGraphControls({
    onRegisterControls,
    setIsTimeRangeModalOpen,
    setIsDatasetModalOpen,
    currentViewRef,
    selectedTimeRange,
  });

  const handleResetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
      setIsZoomed(false);
    }
  };

  // Add double-click to reset zoom
  useEffect(() => {
    let canvas: HTMLCanvasElement | null = null;
    const handleDoubleClick = () => {
      handleResetZoom();
    };

    // Use a small delay to ensure chart is fully initialized
    const timer = setTimeout(() => {
      const chart = chartRef.current;
      if (!chart || !chart.canvas) return;
      canvas = chart.canvas;
      canvas.addEventListener('dblclick', handleDoubleClick);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (canvas) {
        canvas.removeEventListener('dblclick', handleDoubleClick);
      }
    };
  }, []);
  
  // Prepare datasets with error handling
  const datasets = [];
  
  // Add control data if available
  if (Array.isArray(control) && control.length > 0) {
    datasets.push({
      label: `Control`,
      type: "line",
      backgroundColor: "rgba(66,165,245, 0.5)",
      borderColor: "rgba(66,165,245, 0.5)",
      data: control,
    });
  }
  
  // Add average data if available
  if (Array.isArray(average) && average.length > 0) {
    datasets.push({
      label: `Average`,
      type: "line",
      backgroundColor: "rgba(239,83,80, 0.5)",
      borderColor: "rgba(239,83,80, 0.5)",
      data: average,
    });
  }
  
  // Add high resolution data if available
  if (Array.isArray(highResData) && highResData.length > 0) {
    datasets.push({
      label: "Hourly",
      type: "line",
      data: highResData,
      backgroundColor: "rgba(75, 0, 130, 0.5)",
      borderColor: "rgba(75, 0, 130, 0.5)",
    });
  }
  
  // Add GraphCast data if available
  if (graphCastData && Array.isArray(graphCastData.data) && graphCastData.data.length > 0) {
    datasets.push({
      label: "GraphCast",
      type: "line",
      data: graphCastData.data,
      backgroundColor: graphCastData.color || "rgba(255, 140, 0, 0.5)", // Orange with opacity
      borderColor: graphCastData.color || "rgba(255, 140, 0, 0.5)", 
      borderWidth: 2,
      borderDash: [5, 5],
    });
  }
  
  // Add percentile data if available
  try {
    if (data && typeof data === 'object') {
      const dataValues = Object.values(data).filter(arr => Array.isArray(arr));
      const percentileColors = generatePercentileColors(dataValues.length);
      
      const percentileDatasets = dataValues.map((arr, index) => {
        return {
          label: `${10 + index * 20} Percentile`,
          data: arr,
          backgroundColor: percentileColors[index], // Use generated colors
          borderColor: `rgba(255, 255, 255, 1)`,
          borderWidth: 1,
          barPercentage: 0.4,
          categoryPercentage: 2.5,
        };
      });
      
      datasets.push(...percentileDatasets);
    }
  } catch (error) {
    console.error("Error processing percentile data:", error);
  }
  
  const boxplotData = {
    type: "boxplot",
    datasets,
  };
  
  // Initialize with basic window for first render - must be before any conditional returns
  const initialTimeWindow = useMemo(() => {
    if (!data || Object.values(data).length === 0) return { start: new Date(), end: new Date() };
    
    const allTimePoints = [...Object.values(data)]
      .flat()
      .map(item => new Date(item.x).getTime())
      .sort((a, b) => a - b);
    
    return {
      start: new Date(Math.min(...allTimePoints)),
      end: new Date(Math.max(...allTimePoints))
    };
  }, [data]);
  
  // Initialize visibleDatasets
  useEffect(() => {
    const initialVisibility = {};
    boxplotData.datasets.forEach((dataset, index) => {
      initialVisibility[index] = true;
    });
    setVisibleDatasets(initialVisibility);
  }, []);
  
  // Update the selected time range when device type changes
  useEffect(() => {
    setSelectedTimeRange('full');
  }, [isMobile]);
  
  // Calculate time window based on the current time and data
  const getTimeWindow = (hoursToShow) => {
    if (!data || Object.values(data).length === 0) return { start: new Date(), end: new Date() };
    
    // Get all time points from the data
    const allTimePoints = [...Object.values(data)]
      .flat()
      .map(item => new Date(item.x).getTime())
      .sort((a, b) => a - b);
    
    // Find the closest point to current time
    const nowTime = new Date().getTime();
    let closestPointIndex = 0;
    let minDiff = Infinity;
    
    allTimePoints.forEach((time, index) => {
      const diff = Math.abs(time - nowTime);
      if (diff < minDiff) {
        minDiff = diff;
        closestPointIndex = index;
      }
    });
    
    // If we have a full range request, just return min/max of all data
    if (hoursToShow === 'full') {
      // Calculate min and max x values here
      const minX = Math.min(...allTimePoints);
      const maxX = Math.max(...allTimePoints);
      
      return {
        start: new Date(minX),
        end: new Date(maxX)
      };
    }
    
    // Otherwise, use the hours parameter to define the window
    const startTime = allTimePoints[Math.max(0, closestPointIndex)];
    const endTime = startTime + (parseInt(hoursToShow) * 60 * 60 * 1000);
    
    return {
      start: new Date(startTime),
      end: new Date(endTime)
    };
  };
  
  // Apply time range to chart
  const applyTimeRange = (rangeValue) => {
    if (!chartRef.current) return;
    
    const timeWindow = getTimeWindow(rangeValue);
    
    chartRef.current.zoomScale('x', {
      min: timeWindow.start.getTime(),
      max: timeWindow.end.getTime()
    });
    
    // Update the current view reference
    currentViewRef.current = timeWindow;
    
    // Update the selected time range
    setSelectedTimeRange(rangeValue);
  };
  
  // Apply initial time range on component mount
  React.useEffect(() => {
    if (chartRef.current && data) {
      // Set the initial time window
      const timeWindow = getTimeWindow(selectedTimeRange);
      
      chartRef.current.zoomScale('x', {
        min: timeWindow.start.getTime(),
        max: timeWindow.end.getTime()
      });
      
      // Store the initial view reference
      currentViewRef.current = timeWindow;
    }
  }, [data, selectedTimeRange]);
  
  if (!data) {
    return false;
  }

  // Create a safe data array with null checks for each component
  const safeDataArray = [
    ...Object.values(data || {}),
    ...(Array.isArray(highResData) ? [highResData] : []),
    ...(Array.isArray(control) ? control : []),
    ...(Array.isArray(average) ? [average] : []),
    ...(graphCastData && Array.isArray(graphCastData.data) ? [graphCastData.data] : [])
  ].filter(Boolean);

  // Safe flat operation with null checks
  const flatData = safeDataArray.flat().filter(item => item && item.x && item.y !== undefined);

  // Calculate values with error handling
  let maxYValue = 1;
  try {
    maxYValue = flatData.reduce((max, item) => Math.max(max, item.y || 0), 0) * 1.1;
  } catch (error) {
    console.error("Error calculating maxYValue:", error);
  }

  let maxXValue = Date.now();
  try {
    maxXValue = flatData.reduce((max, item) => {
      const timestamp = new Date(item.x).getTime();
      return isNaN(timestamp) ? max : Math.max(max, timestamp);
    }, 0);
  } catch (error) {
    console.error("Error calculating maxXValue:", error);
  }

  let minXValue = Date.now() - 7 * 24 * 60 * 60 * 1000; // Default to 7 days ago
  try {
    minXValue = flatData.reduce((min, item) => {
      const timestamp = new Date(item.x).getTime();
      return isNaN(timestamp) ? min : Math.min(min, timestamp);
    }, Infinity);
    
    // If no data found, use default
    if (minXValue === Infinity) {
      minXValue = Date.now() - 7 * 24 * 60 * 60 * 1000;
    }
  } catch (error) {
    console.error("Error calculating minXValue:", error);
  }

  const options = {
    plugins: {
      legend: { 
        display: false, // Hide legend on all screens
        position: 'top',
      },
        zoom: {
          ...buildZoomOptions({ 
            isMobile, 
            minXValue, 
            maxXValue, 
            minYValue: 0, 
            maxYValue, 
            currentViewRef,
            onZoomReset: () => setIsZoomed(false)
          }),
          onZoomComplete: () => setIsZoomed(true),
          onZoomReset: () => setIsZoomed(false),
        },
      tooltip: buildTooltipOptions({
        timeZone,
        labelFormatter: function (context) {
          const label = context.dataset.label || "";
          const value = context.parsed.y.toFixed(1);
          return `${label}: ${value}`;
        },
      }),

      verticalLine: {},
      annotation: {
        annotations: {
          currentUTCTime: {
            type: "line",
            xMin: new Date().toISOString(),
            xMax: new Date().toISOString(),
            borderColor: "rgb(255, 99, 132)",
            borderWidth: 2,
            label: {
              display: !isMobile,
              content: () => {
                const newTimeDate = new Date();
                return formatDateTimeLabel(newTimeDate, timeZone);
              },
              enabled: true,
              position: "end",
            },
          },
        },
      },
    },
    interaction: {
      mode: "nearest",
      intersect: false,
      axis: "x",
    },
    maintainAspectRatio: false,
    responsive: true,
    spanGaps: true,
    scales: {
      x: {
        stacked: true,
        display: true, //mobile
        type: "time",
        // If on mobile, set the min/max to show just 24 hours by default
        min: initialTimeWindow.start.getTime(),
        max: initialTimeWindow.end.getTime(),
        offset: false,
        grid: {
          ...(isMobile
            ? { drawBorder: true, drawTicks: false, borderWidth: 2, borderColor: "#111827" }
            : {}),
        },
        title: {
          display: true,
          text: "",
        },
        ticks: buildXAxisTicks(
          isMobile ? createMonthDayTickCallback() : createTickCallback(),
          isMobile
        ),
      },

      y: {
        display: true,
        title: {
          display: !isMobile,
          text: yaxis
        },
        min: 0,
        max: Math.ceil(maxYValue),
        ticks: applyMobileTicks({ display: true }, isMobile, {
          mirror: false,
          padding: 4,
          font: { size: 8 },
        }),
        grid: {
          ...(isMobile
            ? { drawBorder: false} // MIKE ADD remove left border line on graph for mobile
            : {}),
        },
        border: isMobile ? { display: false} : undefined, // MIKE ADD remove left border line on graph for mobile
      },
    },
    layout: { // MIKE ADD. shift graph to right on mobile to make space for y axis labels. can be edited
          padding: {
            left: isMobile ? 1 : 0,
          },
        },
  };
  options.plugins.verticalLine = {
    lines: alerts?.map((alert) => {
      return {
        lineValue: alert.alertTimeIndex, // x-value for first line
        lineColor: "blue",
        hoverLineColor: "green",
        lineWidth: 1,
        alertId: alert.alertId,
        onLeave: () => console.log("left"),
      };
    }),
    onHover: (line) => {
      if (line.lineValue !== forecast.shortGefsAlerts.highlightIndex) {
        dispatch(setAlertHighlightIndex(line.lineValue));
      }
    },
    onLeave: () => {
      if (forecast.shortGefsAlerts.highlightIndex !== -1) {
        dispatch(setAlertHighlightIndex(-1));
      }
    },
  };


  if (data) {
    // Toggle dataset visibility
    const toggleDatasetVisibility = (index) => {
      if (chartRef.current) {
        const chart = chartRef.current;
        const meta = chart.getDatasetMeta(index);
        
        // Toggle the visibility
        meta.hidden = !meta.hidden;
        setVisibleDatasets(prev => ({...prev, [index]: !meta.hidden}));
        chart.update();
      }
    };
    
    // Prepare datasets for modal
    const datasetItems = boxplotData.datasets.map((dataset, index) => ({
      id: index.toString(),
      label: dataset.label,
      visible: visibleDatasets[index] !== false,
      color: dataset.backgroundColor
    }));

    return (
      <div className="flex flex-col w-full h-full">
        {!hideGraphControls && (
          <GraphDesktopControls
            selectedTimeRange={selectedTimeRange}
            timeRanges={TIME_RANGES}
            onOpenTimeRange={() => setIsTimeRangeModalOpen(true)}
            onOpenDataset={() => setIsDatasetModalOpen(true)}
            onResetZoom={handleResetZoom}
            isZoomed={isZoomed}
          />
        )}
        
        <div className="flex-1 min-h-0 relative">
          {isMobile && (
            <div className="absolute left-[3px] top-[29px] bottom-[22px] w-0.5 " /> // MIKE ADD. added left 3px to shift line right on mobile and a black line
          )}
          <GraphMobileTimeLabel isMobile={isMobile} timeZone={timeZone} />
          <Bar
            ref={chartRef}
            onMouseOut={() => dispatch(setAlertHighlightIndex(-1))}
            className="h-full w-full"
            options={options}
            data={boxplotData}
            plugins={[verticalLineIntersect, dismissTooltipPlugin]}
          />
        </div>

        <GraphModals
          isDatasetOpen={isDatasetModalOpen}
          isTimeRangeOpen={isTimeRangeModalOpen}
          onCloseDataset={() => setIsDatasetModalOpen(false)}
          onCloseTimeRange={() => setIsTimeRangeModalOpen(false)}
          datasets={datasetItems}
          onToggleVisibility={(id) => toggleDatasetVisibility(parseInt(id))}
          timeRanges={TIME_RANGES}
          selectedTimeRange={selectedTimeRange}
          onSelectTimeRange={applyTimeRange}
          timeZone={timeZone}
          onTimeZoneSelect={(zone) => {
            if (typeof onTimeZoneChange === 'function') {
              onTimeZoneChange(zone);
            }
          }}
        />
      </div>
    );
  }
  return false;
};

export default PrecipPercentile;
